import { useEffect, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  SectionCard,
  FieldLabel,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
} from "@/components/ui-kit";
import { toast } from "sonner";

const empty = { summary: "", last_generated_at: "" };

export default function ExecutiveSummaryModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  async function generate() {
    setGenerating(true);
    try {
      const res = await api.post("/ai/generate-summary", {
        project_id: project.id,
        language: lang,
      });
      const updated = { summary: res.data.summary, last_generated_at: res.data.last_generated_at };
      setV(updated);
      toast.success(lang === "es" ? "Resumen generado" : "Summary generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  }

  const hasSummary = !!(v.summary && v.summary.trim());

  return (
    <>
      <SectionCard
        title={t("exec.title")}
        subtitle={t("exec.subtitle")}
        action={
          <div className="flex items-center gap-3">
            <AIBadge />
            <PrimaryButton
              onClick={generate}
              disabled={generating}
              testId="exec-generate-btn"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              )}
              {generating
                ? t("common.generating")
                : hasSummary
                ? t("exec.regenerate")
                : t("exec.generate")}
            </PrimaryButton>
          </div>
        }
        testId="exec-section"
      >
        {!hasSummary && !generating && (
          <div className="border border-dashed border-[#E8E6E1] rounded-sm p-10 text-center bg-[#F5F4F0]/50">
            <Sparkles className="h-8 w-8 text-[#B89154] mx-auto mb-3" strokeWidth={1.2} />
            <p className="text-sm text-[#5C5B5A] max-w-md mx-auto" data-testid="exec-empty-state">
              {t("exec.empty")}
            </p>
          </div>
        )}

        {(hasSummary || generating) && (
          <div className="space-y-4">
            {v.last_generated_at && (
              <div className="flex items-center justify-between">
                <Overline>{t("exec.last_generated")}</Overline>
                <span className="text-xs text-[#8A8886] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  {new Date(v.last_generated_at).toLocaleString(lang === "es" ? "es-ES" : "en-US")}
                </span>
              </div>
            )}
            <FieldLabel>{t("exec.title")}</FieldLabel>
            <TextArea
              value={v.summary}
              onChange={(x) => setV((p) => ({ ...p, summary: x }))}
              rows={20}
              testId="exec-summary-textarea"
              placeholder={t("exec.placeholder")}
            />
          </div>
        )}
      </SectionCard>
    </>
  );
}
